import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  MATERIAL_DISTRIBUTION_CHANNELS,
  MILLIS,
  PRODUCT_CATEGORIES,
  YEAR_FLAG_VALUES,
  YearFlag,
  type MaterialDistributionChannel,
  type ProductCategory,
} from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useIsCrtVersionComputing } from '@/hooks/queries/crt/useCrtVersions'
import { type Query, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export type DistributionChannelFilterValue = MaterialDistributionChannel

export const pnlTableSearchSchema = z.object({
  crtPeriod: monthStartString.optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  material: z.string().optional(),
  distributionChannel: z.array(z.enum(MATERIAL_DISTRIBUTION_CHANNELS)).optional(),
  isPerMil: z.boolean().optional(),
  year: z.enum(YEAR_FLAG_VALUES).optional(),
  actualsOnly: z.boolean().optional(),
  currency: z.string().optional(),
  pricingAssumptionIds: z.array(z.number()).optional(),
  taxAssumptionIds: z.array(z.number()).optional(),
  manualInvestmentIds: z.array(z.number()).optional(),
  loadedVersionId: z.number().optional(),
  volumeScenarioId: z.number().optional(),
})

export type PnlTableSearch = z.infer<typeof pnlTableSearchSchema>

// Material-scope filters forwarded to the summary endpoints. Absent → aggregate over all materials.
export type PnlFilters = {
  productCategory?: ProductCategory[]
  brand?: string[]
  variant?: string[]
  materialGroupCode?: string[]
  marking?: string[]
  productType?: string[]
  material?: string
  distributionChannel?: DistributionChannelFilterValue[]
  isPerMil?: boolean
  year?: YearFlag
  actualsOnly?: boolean
  pricingAssumptionIds?: number[]
  taxAssumptionIds?: number[]
  manualInvestmentIds?: number[]
  // A loaded version — serves its precomputed per-version denormalized rows (fast path) instead of recomputing.
  loadedVersionId?: number
  // The selected Volume Scenario — recomputes the forecast summary against its adjusted per-SKU volumes.
  volumeScenarioId?: number
}

async function getActualsPnl(crtPeriod: string, filters: PnlFilters, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pnl'][':crtPeriod'].actuals.$get(
    { param: { crtPeriod }, query: stringifyValues(filters) },
    { init: { signal } },
  )
  return await res.json()
}

async function getForecastsPnl(crtPeriod: string, filters: PnlFilters, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pnl'][':crtPeriod'].forecasts.$get(
    { param: { crtPeriod }, query: stringifyValues(filters) },
    { init: { signal } },
  )
  return await res.json()
}

export type PnlResponse = Awaited<ReturnType<typeof getActualsPnl>>

type PnlQueryKey = readonly ['pnl', string, 'actuals' | 'forecasts', PnlFilters]

// The version field a save rewrites in the URL (`markSaved` sets the new id).
const withoutVersionFields = (filters: PnlFilters): PnlFilters => ({
  ...filters,
  loadedVersionId: undefined,
})

const isSameViewIgnoringVersion = (a: PnlFilters, b: PnlFilters) =>
  JSON.stringify(withoutVersionFields(a)) === JSON.stringify(withoutVersionFields(b))

/**
 * Holds a P&L query while its loaded version is being computed by the worker. Fetching then would compute
 * the version live — the heavy path that exhausted the API's heap — for figures that are either already on
 * screen (a save records the current selection, so its figures are the ones displayed) or about to be
 * replaced. So the query is paused, and:
 * - a key already cached (a re-materialized loaded version) keeps its data;
 * - a key the save just produced (the URL gained the new version id) shows the previous key's data, but only
 *   when that key differs in the version fields alone — a filter change during the compute must not show the
 *   old filter's figures under the new one.
 * When the version settles, `useCrtVersions` invalidates the period's P&L queries and this one refetches.
 */
function usePnlVersionHold(crtPeriod: string, filters: PnlFilters) {
  const isVersionComputing = useIsCrtVersionComputing(crtPeriod, filters.loadedVersionId)
  const placeholderData = <T>(previousData: T | undefined, previousQuery?: Query<T>) => {
    if (!isVersionComputing || !previousQuery) return undefined
    const [, , , previousFilters] = previousQuery.queryKey as unknown as PnlQueryKey
    return isSameViewIgnoringVersion(previousFilters, filters) ? previousData : undefined
  }
  return { isVersionComputing, placeholderData }
}

export function useActualsPnl(
  crtPeriod: string,
  filters: PnlFilters = {},
  options?: { enabled?: boolean },
) {
  const { isVersionComputing, placeholderData } = usePnlVersionHold(crtPeriod, filters)
  return useQuery({
    queryKey: ['pnl', crtPeriod, 'actuals', filters],
    queryFn: ({ signal }) => getActualsPnl(crtPeriod, filters, signal),
    enabled: (options?.enabled ?? true) && !isVersionComputing,
    placeholderData,
  })
}

export function useForecastsPnl(
  crtPeriod: string,
  filters: PnlFilters = {},
  options?: { enabled?: boolean },
) {
  const { isVersionComputing, placeholderData } = usePnlVersionHold(crtPeriod, filters)
  return useQuery({
    queryKey: ['pnl', crtPeriod, 'forecasts', filters],
    queryFn: ({ signal }) => getForecastsPnl(crtPeriod, filters, signal),
    enabled: (options?.enabled ?? true) && !isVersionComputing,
    placeholderData,
  })
}

// The per-category summary selection: the loaded version (resolved server-side to its pinned assumptions) and
// the forecast window/view. Absent `versionId` → baseline.
type PnlByCategoryFilters = {
  versionId?: number
  year?: YearFlag
  isPerMil?: boolean
}

async function getPnlByCategory(
  crtPeriod: string,
  filters: PnlByCategoryFilters,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['pnl'][':crtPeriod']['by-category'].$get(
    { param: { crtPeriod }, query: stringifyValues(filters) },
    { init: { signal } },
  )
  return await res.json()
}

export type PnlByCategoryResponse = Awaited<ReturnType<typeof getPnlByCategory>>

/**
 * The per-product-category (FMC/VAPOUR/NRT + roll-up total) actuals + forecast summaries for one period in
 * a single request — the "NTO vs LMC targets" card's data. Passing `versionId` serves that version
 * (its pinned assumptions resolved server-side) rather than the baseline. Cached like the
 * other CRT welcome hooks (import-driven, static within a session).
 */
export function usePnlByCategory(
  crtPeriod: string,
  filters: PnlByCategoryFilters = {},
  options?: { enabled?: boolean },
) {
  // Paused while the version computes, like the summary queries: cached figures stay, nothing is computed live.
  const isVersionComputing = useIsCrtVersionComputing(crtPeriod, filters.versionId)
  return useQuery({
    queryKey: ['pnlByCategory', crtPeriod, filters],
    queryFn: ({ signal }) => getPnlByCategory(crtPeriod, filters, signal),
    enabled: (options?.enabled ?? true) && !isVersionComputing,
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

async function getAssumptionsWalk(crtPeriod: string, versionId: number, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['pnl'][':crtPeriod']['assumptions-walk'].$get(
    { param: { crtPeriod }, query: { versionId: String(versionId) } },
    { init: { signal } },
  )
  return await res.json()
}

export type AssumptionsWalkResponse = Awaited<ReturnType<typeof getAssumptionsWalk>>

/**
 * The four combined full-year NTO figures (`baseline`, `pricing`, `manual`, `version`) the "NTO Assumptions
 * Walk" card plots, computed server-side in one request for the selected `versionId`. Cached like the
 * other CRT welcome hooks.
 */
export function useAssumptionsWalk(
  crtPeriod: string,
  versionId: number | undefined,
  options?: { enabled?: boolean },
) {
  // Paused while the version computes, like the summary queries: cached figures stay, nothing is computed live.
  const isVersionComputing = useIsCrtVersionComputing(crtPeriod, versionId)
  return useQuery({
    queryKey: ['assumptionsWalk', crtPeriod, versionId],
    queryFn: ({ signal }) => getAssumptionsWalk(crtPeriod, versionId as number, signal),
    enabled: (options?.enabled ?? true) && versionId != null && !isVersionComputing,
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

/**
 * Query options for the per-(material × sub-channel) CSV export. `distributionChannel` picks the file:
 * `export` → CDF, `domestic` → everything else. Covers all SKUs for the channel (page filters do not
 * apply). The response can be a CSV or, when large, a server-zipped bundle.
 */
export const pnlExportQueryOptions = (
  crtPeriod: string,
  distributionChannel: MaterialDistributionChannel,
) => ({
  queryKey: ['pnlExport', crtPeriod, distributionChannel],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchCsvExport(
      `/crt/pnl/${crtPeriod}/export?distributionChannel=${distributionChannel}`,
      signal,
    ),
})

// The assumption-sandbox selection forwarded to the "Export View" so the CSV mirrors the on-screen view
// (a loaded version or a layered selection). Absent → baseline export.
type PnlExportAssumptions = {
  pricingAssumptionIds?: number[]
  taxAssumptionIds?: number[]
  manualInvestmentIds?: number[]
}

/**
 * Query options for the "Export View" CSV: every SKU across both channels, with each material's Fuze
 * attributes (product category, material group, brand, variant, product type, marking, pack content
 * size, status) prepended before the full KPI columns. Covers all SKUs (page filters do not apply).
 * `assumptions` pins the current view's assumption ids so the CSV matches the loaded version; it is part of
 * the query key so a load/clear yields a distinct cached export.
 * The response can be a CSV or, when large, a server-zipped bundle.
 */
export const pnlMaterialsExportQueryOptions = (
  crtPeriod: string,
  assumptions?: PnlExportAssumptions,
) => {
  const params = Object.entries(stringifyValues(assumptions ?? {})).filter(
    (entry): entry is [string, string] => entry[1] != null,
  )
  const query = new URLSearchParams(params).toString()
  const url = `/crt/pnl/${crtPeriod}/export-view${query ? `?${query}` : ''}`

  return {
    queryKey: ['pnlMaterialsExport', crtPeriod, assumptions],
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchCsvExport(url, signal),
  }
}

/**
 * Query options for the cash dashboard's AR Report CSV: the finance workbook's AR Report tab, for exactly
 * what the page shows — the same filters, view and assumption sandbox the summary queries send. Every
 * request is a live compute, so the key carries the whole selection.
 */
export const arReportQueryOptions = (crtPeriod: string, filters: PnlFilters) => {
  const params = Object.entries(stringifyValues(filters)).filter(
    (entry): entry is [string, string] => entry[1] != null,
  )
  const query = new URLSearchParams(params).toString()
  const url = `/crt/pnl/${crtPeriod}/ar-report${query ? `?${query}` : ''}`

  return {
    queryKey: ['pnlArReport', crtPeriod, filters],
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchCsvExport(url, signal),
  }
}
